import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TextPlugin } from 'gsap/TextPlugin';
import Hammer from 'hammerjs';

gsap.registerPlugin(ScrollTrigger, TextPlugin);

export function initApp() {
    // Text Loop Animation
    (function initTextLoop() {
        const rotatingEl = document.getElementById('textLoopRotating');
        if (!rotatingEl) return;

        const words = ['wilder', 'cooler', 'krasser', 'verrückter'];
        let index = 0;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Pre-calculate widths to avoid forced reflow during animation
        const widthMap = {};
        const measurer = document.createElement('span');
        measurer.className = 'text-loop-word';
        measurer.style.position = 'absolute';
        measurer.style.visibility = 'hidden';
        rotatingEl.appendChild(measurer);
        words.forEach(w => {
            measurer.textContent = w;
            widthMap[w] = measurer.offsetWidth;
        });
        measurer.remove();

        function setWord(word) {
            rotatingEl.style.width = '0px';

            setTimeout(() => {
                rotatingEl.innerHTML = '';
                const newSpan = document.createElement('span');
                newSpan.className = 'text-loop-word';
                newSpan.textContent = word;
                rotatingEl.appendChild(newSpan);
                rotatingEl.style.width = widthMap[word] + 'px';
            }, 300);
        }

        const firstSpan = document.createElement('span');
        firstSpan.className = 'text-loop-word';
        firstSpan.textContent = words[0];
        rotatingEl.appendChild(firstSpan);
        rotatingEl.style.width = widthMap[words[0]] + 'px';

        // Bei reduzierter Bewegung bleibt das erste Wort stehen: kein Intervall.
        if (!reduceMotion) {
            setInterval(() => {
                index = (index + 1) % words.length;
                setWord(words[index]);
            }, 3000);
        }
    })();

    let konamiSequence = [];
    const konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA'];

    initializeGSAPAnimations();
    initializeTouchGestures();
    initializeMobileNavigation();
    initializeThemeSystem();
    initializeSkillProgressBars();
    initializeEasterEggs();
    initializeSmoothScroll();
    initializeContactButton();

    function initializeGSAPAnimations() {
        const loaderStart = Date.now();
        const MIN_LOADER_MS = 300;

        function hideLoaderAndAnimate() {
            const elapsed = Date.now() - loaderStart;
            const remaining = Math.max(0, MIN_LOADER_MS - elapsed);

            gsap.to('.loader', {
                opacity: 0,
                duration: 0.6,
                delay: remaining / 1000,
                ease: "power2.out",
                onComplete: () => {
                    document.querySelector('.loader').style.display = 'none';
                }
            });

            const tl = gsap.timeline({ delay: (remaining / 1000) + 0.4 });
            tl.from('header', {
                y: -100,
                opacity: 0,
                duration: 1,
                ease: "bounce.out"
            });
        }

        if (document.readyState === 'complete') {
            hideLoaderAndAnimate();
        } else {
            window.addEventListener('load', hideLoaderAndAnimate);
        }

        gsap.from('#about p', {
            scrollTrigger: {
                trigger: '#about p',
                start: 'top 85%',
                toggleActions: 'play none none reverse'
            },
            y: 50,
            opacity: 0,
            duration: 0.8,
            stagger: 0.2,
            ease: "power2.out"
        });

        gsap.from('#contact .contact-actions', {
            scrollTrigger: {
                trigger: '#contact .contact-actions',
                start: 'top 85%',
                toggleActions: 'play none none reverse'
            },
            y: 100,
            opacity: 0,
            duration: 1,
            ease: "power3.out"
        });

        document.querySelectorAll('nav a, .mobile-nav-links a').forEach(link => {
            // Nur die Skalierung animieren: GSAP wuerde eine aufgeloeste Farbe
            // als Inline-Style schreiben und damit .nav-item:hover/.active aushebeln.
            link.addEventListener('mouseenter', () => {
                gsap.to(link, {
                    scale: 1.1,
                    duration: 0.3,
                    ease: "power2.out"
                });
            });

            link.addEventListener('mouseleave', () => {
                gsap.to(link, {
                    scale: 1,
                    duration: 0.3,
                    ease: "power2.out"
                });
            });
        });

        document.querySelectorAll('button').forEach(button => {
            button.addEventListener('mouseenter', () => {
                gsap.to(button, {
                    scale: 1.05,
                    duration: 0.2,
                    ease: "power2.out"
                });
            });

            button.addEventListener('mouseleave', () => {
                gsap.to(button, {
                    scale: 1,
                    duration: 0.2,
                    ease: "power2.out"
                });
            });

            button.addEventListener('click', () => {
                gsap.to(button, {
                    scale: 0.95,
                    duration: 0.1,
                    ease: "power2.out",
                    yoyo: true,
                    repeat: 1
                });
            });
        });

        gsap.fromTo('.scroll-to-top',
            { scale: 0, rotation: -180 },
            {
                scale: 1,
                rotation: 0,
                duration: 0.5,
                ease: "back.out(1.7)",
                paused: true,
                id: "scrollToTopAnim"
            }
        );

        initializeLottieAnimations();
        initializeNavItems();
    }

    function initializeLottieAnimations() {
        // Lottie removed - saves ~500KB for a simple animated circle
    }

    // Markiert die angeklickte Sektion in der Navigation. Das Scrollen selbst
    // macht initializeSmoothScroll(), der dafuer bereits gegen fehlende Ziele
    // und nachtraeglich umgeschriebene hrefs abgesichert ist.
    function initializeNavItems() {
        document.querySelectorAll('.nav-item').forEach((item) => {
            item.addEventListener('click', () => {
                document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
                item.classList.add('active');
            });
        });
    }

    function initializeTouchGestures() {
        try {
            const hammer = new Hammer(document.body, {
                touchAction: 'pan-y',
                recognizers: [
                    [Hammer.Swipe, { direction: Hammer.DIRECTION_HORIZONTAL }],
                    [Hammer.Tap, { taps: 2 }]
                ]
            });

            hammer.get('swipe').set({
                direction: Hammer.DIRECTION_HORIZONTAL,
                threshold: 50,
                velocity: 0.3
            });
            hammer.get('tap').set({ taps: 2 });

            hammer.on('swipeleft', (e) => {
                if (window.innerWidth <= 768 && Math.abs(e.deltaY) < 50) {
                    e.preventDefault();
                    const currentSection = getCurrentSection();
                    const nextSection = getNextSection(currentSection);
                    if (nextSection) scrollToSection(nextSection);
                }
            });

            hammer.on('swiperight', (e) => {
                if (window.innerWidth <= 768 && Math.abs(e.deltaY) < 50) {
                    e.preventDefault();
                    const currentSection = getCurrentSection();
                    const prevSection = getPrevSection(currentSection);
                    if (prevSection) scrollToSection(prevSection);
                }
            });

            hammer.on('tap', (e) => {
                if (e.tapCount === 2) {
                    createTouchParticles(e.center.x, e.center.y);
                }
            });

        } catch (error) {
            console.log('Touch gesture initialization failed:', error);
        }
    }

    function handleTouchStart(e) {
        const touch = e.touches[0];
        createTouchParticles(touch.clientX, touch.clientY);
    }

    function handleTouchMove(e) {
        if (!e.defaultPrevented) {
            const touch = e.touches[0];
            if (Math.random() > 0.95) {
                createTouchParticles(touch.clientX, touch.clientY);
            }
        }
    }

    function createTouchParticles(x, y) {
        for (let i = 0; i < 3; i++) {
            const particle = document.createElement('div');
            particle.className = 'comet-particle';
            particle.style.left = x + (Math.random() - 0.5) * 20 + 'px';
            particle.style.top = y + (Math.random() - 0.5) * 20 + 'px';
            document.body.appendChild(particle);
            setTimeout(() => particle.remove(), 1000);
        }
    }

    function initializeMobileNavigation() {
        const hamburger = document.querySelector('.hamburger');
        const mobileNav = document.querySelector('.mobile-nav');
        const mobileLinks = document.querySelectorAll('.mobile-nav-links a, .mobile-nav-links button');

        if (!hamburger || !mobileNav) return;

        hamburger.addEventListener('click', (e) => {
            e.stopPropagation();
            hamburger.classList.toggle('active');
            mobileNav.classList.toggle('active');

            if (mobileNav.classList.contains('active')) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }, { passive: true });

        mobileLinks.forEach(link => {
            link.addEventListener('click', () => {
                hamburger.classList.remove('active');
                mobileNav.classList.remove('active');
                document.body.style.overflow = '';
            }, { passive: true });
        });

        document.addEventListener('click', (e) => {
            if (!hamburger.contains(e.target) && !mobileNav.contains(e.target)) {
                hamburger.classList.remove('active');
                mobileNav.classList.remove('active');
                document.body.style.overflow = '';
            }
        }, { passive: true });

        mobileNav.addEventListener('click', (e) => {
            e.stopPropagation();
        }, { passive: true });

        mobileNav.addEventListener('touchstart', (e) => {
            e.stopPropagation();
        }, { passive: true });

        mobileNav.addEventListener('touchmove', (e) => {
            e.stopPropagation();
        }, { passive: true });
    }

    function initializeContactButton() {
        const mailLink = document.getElementById('mailLink');
        const copyBtn = document.getElementById('copyMail');
        const copyLabel = document.getElementById('copyLabel');
        const copyStatus = document.getElementById('copyStatus');
        const freqAddr = document.getElementById('freqAddr');
        if (!mailLink || !copyBtn || !copyLabel || !copyStatus) return;

        const address = `${mailLink.dataset.u}@${mailLink.dataset.d}`;
        mailLink.href = `mailto:${address}`;
        mailLink.setAttribute('aria-label', `E-Mail an ${address} schreiben`);
        mailLink.removeAttribute('title');

        // Das Label bleibt die Aktion; die Adresse steht jetzt in der Ablesezeile darunter
        if (freqAddr) {
            freqAddr.textContent = `${mailLink.dataset.u} [at] ${mailLink.dataset.d}`;
        }

        const defaultLabel = copyLabel.textContent;
        let resetTimer = null;

        function flash(text) {
            copyLabel.textContent = text;
            copyStatus.textContent = text;
            copyBtn.classList.add('is-copied');
            clearTimeout(resetTimer);
            resetTimer = setTimeout(() => {
                copyLabel.textContent = defaultLabel;
                // Clear to empty rather than restoring the resting label —
                // that avoids a spurious second announcement, while still
                // guaranteeing the next success write is a real content
                // change, so a repeat copy re-announces.
                copyStatus.textContent = '';
                copyBtn.classList.remove('is-copied');
            }, 2000);
        }

        function selectAddress() {
            if (!freqAddr) return;
            // Im Fallback zaehlt Kopierbarkeit mehr als die [at]-Schreibweise:
            // wir zeigen die echte Adresse und markieren sie.
            freqAddr.textContent = address;
            const range = document.createRange();
            range.selectNodeContents(freqAddr);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
        }

        copyBtn.addEventListener('click', async () => {
            try {
                if (!navigator.clipboard) throw new Error('Clipboard API not available');
                await navigator.clipboard.writeText(address);
                flash('✓ Kopiert!');
            } catch {
                selectAddress();
                flash('Zum Kopieren markiert');
            }
        });
    }

    const SPECTRAL_IDS = ['O', 'G', 'K', 'M', 'N'];
    const LEGACY_THEME_MAP = {
        default: 'G',
        sunset: 'K',
        ocean: 'O',
        cyberpunk: 'N',
        forest: 'M'
    };

    // Uebersetzt eine gespeicherte Theme-ID in eine Spektralklasse.
    // Alte IDs kommen aus localStorage von Bestandsbesuchern.
    function migrateThemeId(stored) {
        if (SPECTRAL_IDS.includes(stored)) return stored;
        return LEGACY_THEME_MAP[stored] || 'G';
    }

    // Liefert die Spektralklasse zur aktuellen Tageszeit.
    function getTimeBasedTheme() {
        const hour = new Date().getHours();
        if (hour >= 6 && hour < 12) return 'G';
        if (hour >= 12 && hour < 18) return 'K';
        if (hour >= 18 && hour < 22) return 'O';
        return 'N';
    }

    function initializeThemeSystem() {
        const buttons = document.querySelectorAll('.spectral button[data-theme]');

        function select(theme, persist = true) {
            applyTheme(theme);
            buttons.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.theme === theme)));
            if (persist) localStorage.setItem('selectedTheme', theme);
        }

        buttons.forEach(button => {
            button.addEventListener('click', () => select(button.dataset.theme));
        });

        const stored = localStorage.getItem('selectedTheme');

        if (stored === null) {
            // Noch keine Wahl getroffen: die Tageszeit entscheidet - und wird bewusst
            // NICHT festgeschrieben, damit sie beim naechsten Besuch neu greifen kann.
            const auto = localStorage.getItem('autoTheme') !== 'false';
            select(auto ? getTimeBasedTheme() : 'G', false);
        } else {
            // Gespeicherte Wahl: alte ID uebersetzen und die Uebersetzung einmal festschreiben.
            const migrated = migrateThemeId(stored);
            select(migrated, migrated !== stored);
        }
    }

    function applyTheme(theme) {
        document.body.dataset.theme = theme;
        document.body.animate([
            { opacity: 0.8 },
            { opacity: 1 }
        ], {
            duration: 300,
            easing: 'ease-out'
        });
    }

    function initializeSkillProgressBars() {
        const skillProgressBars = document.querySelectorAll('.skill-progress');

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const progressBar = entry.target;
                    const width = progressBar.dataset.width;

                    progressBar.animate([
                        { width: '0%' },
                        { width: width + '%' }
                    ], {
                        duration: 2000,
                        easing: 'ease-out',
                        fill: 'forwards'
                    });

                    observer.unobserve(progressBar);
                }
            });
        }, { threshold: 0.5 });

        skillProgressBars.forEach(bar => observer.observe(bar));
    }

    function initializeEasterEggs() {
        document.addEventListener('keydown', (e) => {
            konamiSequence.push(e.code);
            if (konamiSequence.length > konamiCode.length) {
                konamiSequence.shift();
            }

            if (JSON.stringify(konamiSequence) === JSON.stringify(konamiCode)) {
                activateKonamiMode();
            }
        });

        let clickCount = 0;
        document.querySelector('h1').addEventListener('click', () => {
            clickCount++;
            if (clickCount === 3) {
                showSecretMessage();
                clickCount = 0;
            }
            setTimeout(() => clickCount = 0, 1000);
        });
    }

    function activateKonamiMode() {
        document.body.classList.add('konami-mode');
        showSecretMessage();

        for (let i = 0; i < 50; i++) {
            setTimeout(() => {
                const x = Math.random() * window.innerWidth;
                const y = Math.random() * window.innerHeight;
                createTouchParticles(x, y);
            }, i * 50);
        }

        setTimeout(() => {
            document.body.classList.remove('konami-mode');
        }, 5000);
    }

    function showSecretMessage() {
        const secretMessage = document.getElementById('secretMessage');
        secretMessage.classList.add('show');

        setTimeout(() => {
            secretMessage.classList.remove('show');
        }, 3000);
    }

    function initializeSmoothScroll() {
        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', function (e) {
                // Re-check at click time: the selector above is a snapshot taken
                // at load. #mailLink starts out with a placeholder "#contact" and
                // is rewritten to mailto: once initializeContactButton runs, so a
                // bound anchor's href is not guaranteed to still be a selector.
                const href = this.getAttribute('href');
                if (!href || href.length < 2 || !href.startsWith('#')) return;

                e.preventDefault();
                const target = document.querySelector(href);
                if (target) {
                    target.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            });
        });
    }

    function getCurrentSection() {
        const sections = document.querySelectorAll('section');
        let current = 'home';

        sections.forEach(section => {
            const rect = section.getBoundingClientRect();
            if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
                current = section.id;
            }
        });

        return current;
    }

    function getNextSection(current) {
        const sections = ['home', 'about', 'prudii', 'contact'];
        const currentIndex = sections.indexOf(current);
        return sections[currentIndex + 1] || null;
    }

    function getPrevSection(current) {
        const sections = ['home', 'about', 'prudii', 'contact'];
        const currentIndex = sections.indexOf(current);
        return sections[currentIndex - 1] || null;
    }

    function scrollToSection(sectionId) {
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }

    // Parallax
    const parallaxElements = document.querySelectorAll(".parallax");
    window.addEventListener("scroll", () => {
        const scrollY = window.scrollY;
        parallaxElements.forEach((el) => {
            const speed = parseFloat(el.dataset.parallaxSpeed) || 0.1;
            const maxOffset = 50;
            let offset = scrollY * speed;
            if (offset > maxOffset) offset = maxOffset;
            if (offset < -maxOffset) offset = -maxOffset;
            el.style.transform = `translateY(${offset}px)`;
        });
    });

    const aboutImage = document.querySelector('img[alt="Über mich Bild"]');
    aboutImage.addEventListener('mouseleave', () => {
        aboutImage.style.transform = 'scaleX(-1)';
        aboutImage.style.filter = 'brightness(1)';
    });

    document.querySelectorAll('.skill-tag').forEach(tag => {
        tag.addEventListener('mouseleave', () => {
            tag.style.transform = 'none';
            const isLightMode = document.body.classList.contains('light-mode');
            tag.style.background = isLightMode ? '#d1d5db' : 'rgba(31, 41, 55, 1)';
        });
    });

    // Section visibility observer
    const sections = document.querySelectorAll('section');
    const sectionObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('visible');
                observer.unobserve(entry.target);
            }
        });
    }, { threshold: 0.2 });

    sections.forEach(section => {
        sectionObserver.observe(section);
    });

    // Scroll to top
    const scrollToTopBtn = document.querySelector('.scroll-to-top');
    window.addEventListener('scroll', () => {
        if (window.scrollY > 300) {
            scrollToTopBtn.classList.add('visible');
        } else {
            scrollToTopBtn.classList.remove('visible');
        }
    });

    scrollToTopBtn.addEventListener('click', () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    });

    // Active nav highlighting
    const navLinks = document.querySelectorAll('nav a');
    const sectionsNav = document.querySelectorAll('section');

    window.addEventListener('scroll', () => {
        let current = '';
        sectionsNav.forEach(section => {
            const sectionTop = section.offsetTop;
            const sectionHeight = section.clientHeight;
            if (window.scrollY >= sectionTop - sectionHeight / 3) {
                current = section.getAttribute('id');
            }
        });

        navLinks.forEach(link => {
            link.classList.remove('active');
            if (link.getAttribute('href').slice(1) === current) {
                link.classList.add('active');
            }
        });
    });
}

// Effects that run outside DOMContentLoaded
export function initEffects() {
    // Crystal spark effect
    document.querySelectorAll('.skill-crystal').forEach(tag => {
        tag.addEventListener('mouseenter', () => {
            for (let i = 0; i < 8; i++) {
                const spark = document.createElement('div');
                spark.className = 'crystal-spark';
                const angle = Math.random() * Math.PI * 2;
                const distance = 20 + Math.random() * 30;
                spark.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
                spark.style.setProperty('--dy', `${Math.sin(angle) * distance}px`);
                spark.style.left = `${tag.offsetWidth / 2}px`;
                spark.style.top = `${tag.offsetHeight / 2}px`;
                tag.appendChild(spark);
                setTimeout(() => spark.remove(), 500);
            }
        });
    });

    // Black hole button effect
    document.querySelectorAll('.black-hole-btn').forEach(btn => {
        btn.addEventListener('mouseenter', () => {
            for (let i = 0; i < 10; i++) {
                const particle = document.createElement('div');
                particle.className = 'black-hole-particle';
                const angle = Math.random() * Math.PI * 2;
                const distance = 30 + Math.random() * 20;
                particle.style.left = `${btn.offsetWidth / 2 + Math.cos(angle) * distance}px`;
                particle.style.top = `${btn.offsetHeight / 2 + Math.sin(angle) * distance}px`;
                particle.style.setProperty('--dx', `${-Math.cos(angle) * distance}px`);
                particle.style.setProperty('--dy', `${-Math.sin(angle) * distance}px`);
                btn.appendChild(particle);
                setTimeout(() => particle.remove(), 500);
            }
        });
    });

    // Throttled comet particle trail on mouse move
    let lastCometTime = 0;
    document.addEventListener('mousemove', (e) => {
        const now = Date.now();
        if (now - lastCometTime < 50) return; // Throttle to max 20fps
        lastCometTime = now;
        const particle = document.createElement('div');
        particle.className = 'comet-particle';
        particle.style.left = `${e.clientX}px`;
        particle.style.top = `${e.clientY}px`;
        document.body.appendChild(particle);
        setTimeout(() => particle.remove(), 1000);
    });
}

